/**
 * Firebase email actions (verify email, reset password, recover email) for both
 * sign-in projects. Each project's email templates point their action URL here;
 * the link's apiKey says which project issued it.
 */

import React, { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { applyActionCode, checkActionCode, confirmPasswordReset, sendEmailVerification, verifyPasswordResetCode, type Auth, type User } from 'firebase/auth';
import Logo from '../ui/logo';
import { AnimatedBackground } from '../ui/animated-background';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { authFor, authProjectForApiKey } from '../../services/firebase';
import { frontDoorOn } from '../../lib/frontDoor/flag';
import { readDraft } from '../../lib/frontDoor/draft';

type View =
  | { kind: 'working' }
  | { kind: 'verified'; signedIn: boolean }
  | { kind: 'reset'; email: string }
  | { kind: 'reset-done' }
  | { kind: 'recovered'; email: string }
  | { kind: 'error'; message: string; resendTo?: User };

function continueUrl(raw: string | null): string {
  if (!raw) return '/';
  try {
    const url = new URL(raw, window.location.origin);
    const ours = url.protocol === 'https:' && (url.hostname === '4klabs.ai' || url.hostname.endsWith('.4klabs.ai'));
    if (url.origin === window.location.origin || ours) return url.href;
  } catch {
    /* not a URL */
  }
  return '/';
}

function actionErrorMessage(error: unknown): string {
  const code = (error as { code?: string })?.code ?? '';
  if (code === 'auth/expired-action-code') return 'This link has expired. Request a new one and try again.';
  if (code === 'auth/invalid-action-code') return 'This link has already been used or is not valid.';
  if (code === 'auth/weak-password') return 'Use a password of at least 6 characters.';
  return 'Something went wrong. Request a new link and try again.';
}

/** The user signed in on this browser for that project, reloaded so emailVerified is current; null if none. */
async function signedInUser(auth: Auth): Promise<User | null> {
  await auth.authStateReady();
  const user = auth.currentUser;
  if (!user) return null;
  await user.reload().catch(() => {});
  return user;
}

export const AuthActionPage: React.FC = () => {
  const [params] = useState(() => new URLSearchParams(window.location.search));
  const [view, setView] = useState<View>({ kind: 'working' });
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [resend, setResend] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');

  const mode = params.get('mode');
  const oobCode = params.get('oobCode') ?? '';
  const project = authProjectForApiKey(params.get('apiKey'));
  const next = continueUrl(params.get('continueUrl'));

  useEffect(() => {
    if (!project || !oobCode) {
      setView({ kind: 'error', message: 'This link is not valid. Request a new one and try again.' });
      return;
    }
    const auth = authFor(project);
    (async () => {
      try {
        if (mode === 'verifyEmail') {
          try {
            await applyActionCode(auth, oobCode);
          } catch (err) {
            // An already-used link for an email that is verified is a success; anything else offers a resend.
            const user = await signedInUser(auth).catch(() => null);
            if (user?.emailVerified) {
              setView({ kind: 'verified', signedIn: true });
              return;
            }
            setView({ kind: 'error', message: actionErrorMessage(err), resendTo: user ?? undefined });
            return;
          }
          const user = await signedInUser(auth).catch(() => null);
          // A fresh token carries email_verified, so the app's verify gate clears without signing out.
          if (user) await user.getIdToken(true).catch(() => {});
          setView({ kind: 'verified', signedIn: Boolean(user) });
        } else if (mode === 'resetPassword') {
          setView({ kind: 'reset', email: await verifyPasswordResetCode(auth, oobCode) });
        } else if (mode === 'recoverEmail') {
          const info = await checkActionCode(auth, oobCode);
          await applyActionCode(auth, oobCode);
          setView({ kind: 'recovered', email: info.data.email ?? '' });
        } else {
          setView({ kind: 'error', message: 'This link is not valid. Request a new one and try again.' });
        }
      } catch (err) {
        setView({ kind: 'error', message: actionErrorMessage(err) });
      }
    })();
  }, [mode, oobCode, project, next]);

  const handleResend = async (user: User) => {
    setResend('sending');
    try {
      await sendEmailVerification(user);
      setResend('sent');
    } catch {
      setResend('failed');
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!project) return;
    if (password.length < 6) {
      setFormError('Use a password of at least 6 characters.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await confirmPasswordReset(authFor(project), oobCode, password);
      setView({ kind: 'reset-done' });
    } catch (err) {
      setFormError(actionErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  // A front-door draft in this browser: the tab that signed up starts it once it sees the verification.
  const startingElsewhere = view.kind === 'verified' && view.signedIn && frontDoorOn() && readDraft() !== null;

  return (
    <div className="min-h-screen relative">
      <AnimatedBackground />
      <div className="relative flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-sm space-y-6">
          <div className="flex justify-center">
            <Logo variant="modern" />
          </div>

          {view.kind === 'working' && (
            <Card>
              <CardContent className="flex justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Working" />
              </CardContent>
            </Card>
          )}

          {view.kind === 'verified' && (
            <Card>
              <CardHeader className="text-center">
                <CheckCircle2 className="mx-auto h-8 w-8 text-primary" />
                <CardTitle className="text-2xl">Email verified</CardTitle>
                <CardDescription>
                  {startingElsewhere
                    ? 'Your video is starting in your other tab.'
                    : view.signedIn ? 'You are all set.' : 'Sign in to start creating with 4K Labs.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button className="w-full" onClick={() => window.location.replace(view.signedIn ? next : '/login')}>
                  {startingElsewhere ? 'Continue here' : view.signedIn ? 'Continue to 4K Labs' : 'Sign in'}
                </Button>
              </CardContent>
            </Card>
          )}

          {view.kind === 'reset' && (
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">Choose a new password</CardTitle>
                <CardDescription className="break-all">for {view.email}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleReset} className="space-y-4">
                  {formError && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{formError}</AlertDescription>
                    </Alert>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      placeholder="At least 6 characters"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={saving}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={saving}>
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save password
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {(view.kind === 'reset-done' || view.kind === 'recovered') && (
            <Card>
              <CardHeader className="text-center">
                <CheckCircle2 className="mx-auto h-8 w-8 text-primary" />
                <CardTitle className="text-2xl">
                  {view.kind === 'reset-done' ? 'Password updated' : 'Email restored'}
                </CardTitle>
                <CardDescription className="break-all">
                  {view.kind === 'reset-done'
                    ? 'Sign in with your new password.'
                    : `Your sign-in email is ${view.email} again. If you did not ask for the change, reset your password.`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button className="w-full" onClick={() => window.location.replace('/login')}>
                  Sign in
                </Button>
              </CardContent>
            </Card>
          )}

          {view.kind === 'error' && (
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">This link did not work</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{view.message}</AlertDescription>
                </Alert>
                {view.resendTo && resend !== 'sent' && (
                  <Button className="w-full" disabled={resend === 'sending'} onClick={() => handleResend(view.resendTo!)}>
                    {resend === 'sending' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Resend verification email
                  </Button>
                )}
                {resend === 'sent' && (
                  <p className="text-center text-sm text-muted-foreground">
                    We sent a new verification link to {view.resendTo?.email}. Check your inbox.
                  </p>
                )}
                {resend === 'failed' && (
                  <p className="text-center text-sm text-destructive">Could not send the email. Try again in a minute.</p>
                )}
                <Button variant="outline" className="w-full" onClick={() => window.location.replace('/login')}>
                  Back to sign in
                </Button>
              </CardContent>
            </Card>
          )}

          <p className="text-center text-xs text-muted-foreground">© {new Date().getFullYear()} 4K Labs</p>
        </div>
      </div>
    </div>
  );
};
